import type { LucideIcon } from 'lucide-react';
import type { VizAccent } from '@/design/VizAccents';
import type { IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import type { AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';
import { createInterfaceToken } from '@bmai/shared';
import type {
    EnrichedPool,
    GetApiLicensesCostSummary200ResponseByPlatformInner,
    LicenseAssignment,
    LicenseCostRecord,
    LicenseOptimizationDto,
} from '@bmai/licenses-api-client';
import type { SignalTuning } from '@/runtime-config/SignalTuning';

/**
 * The landing-page "signal tile" system. Each surface declares a catalog of
 * {@link SignalTileDef} — a small, self-describing dashboard card. A tile always
 * renders deterministic illustrative data first (so the page is never blank or
 * jumpy) and, when a `live` loader is provided, swaps in real data from the
 * analytics OLAP gateway or the Licenses API once it resolves. This lets a
 * surface mix genuinely live metrics with credible samples for measures the
 * backend can't yet answer.
 */

/** The visual form of a tile's body. */
export type SignalKind = 'kpi' | 'bars' | 'donut' | 'heatmap' | 'gauge' | 'alerts' | 'trend';

/** How a numeric value is rendered. `percent` expects a fraction in [0,1]. */
export type SignalValueFormat = 'count' | 'compact' | 'percent' | 'currency' | 'days' | 'ratio' | 'raw';

/** A movement indicator shown beside a KPI headline. */
export interface SignalDelta {
    /** Pre-formatted magnitude, e.g. "+12%" or "-340". */
    label: string;
    direction: 'up' | 'down' | 'flat';
    /** Whether the movement reads as good (green), bad (red), or neutral (gray). */
    tone: 'good' | 'bad' | 'neutral';
}

/** A single labelled value for bars / donut / trend bodies. */
export interface SignalDatum {
    label: string;
    value: number;
}

export type SignalSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info';

/** A single row in an alert-list body. */
export interface SignalAlert {
    label: string;
    detail?: string;
    severity: SignalSeverity;
    /** Small right-aligned metadata, e.g. a count or relative time. */
    meta?: string;
}

export interface SignalHeatRow {
    label: string;
    /** One raw value per column; intensity is normalized against the grid peak. */
    values: number[];
}

export interface SignalHeat {
    columns: string[];
    rows: SignalHeatRow[];
    format?: SignalValueFormat;
}

export interface SignalGaugeData {
    /** Fill fraction in [0,1]. */
    fraction: number;
    /** Pre-formatted central value, e.g. "87%". */
    valueText: string;
    caption?: string;
}

/**
 * The resolved data payload for a tile. Which fields are read depends on the
 * tile's {@link SignalKind}; a sample/live producer fills only what its kind needs.
 */
export interface SignalData {
    headline?: string;
    caption?: string;
    delta?: SignalDelta;
    spark?: number[];

    series?: SignalDatum[];
    format?: SignalValueFormat;

    heat?: SignalHeat;

    gauge?: SignalGaugeData;

    alerts?: SignalAlert[];
    alertCount?: number;
}

/** A seeded, deterministic RNG handed to each tile's `sample()` so fake data is stable across renders. */
export interface SignalRng {
    /** Float in [0,1). */
    next(): number;
    /** Integer in [min,max] inclusive. */
    int(min: number, max: number): number;
    /** Float in [min,max). */
    float(min: number, max: number): number;
    /** A deterministic pick from a non-empty list. */
    pick<T>(items: readonly T[]): T;
}

/**
 * Read-only license portfolio data available to live signal loaders. Bound to a
 * concrete implementation at the composition root, keeping this shared engine
 * decoupled from the feature that provides it.
 */
export interface ISignalLicenseSource {
    listPools(): Promise<EnrichedPool[]>;
    listAllAssignments(): Promise<LicenseAssignment[]>;
    getOptimization(days?: number): Promise<LicenseOptimizationDto>;
    getPlatformSpend(periodStart: string, periodEnd: string): Promise<GetApiLicensesCostSummary200ResponseByPlatformInner[]>;
    getCostRecords(periodStart: string, periodEnd: string, limit?: number): Promise<LicenseCostRecord[]>;
}

export const ISignalLicenseSourceToken = createInterfaceToken<ISignalLicenseSource>('ISignalLicenseSource');

/** Live data dependencies passed to a tile's `live()` loader. */
export interface SignalContext {
    gateway: IAnalyticsGateway;
    licenses: ISignalLicenseSource;
    /** The surface's ambient assist filters (empty on surfaces without assist). */
    filters: AnalyticsFilter[];
    /** Operational tuning knobs (windows, thresholds) from the BFF runtime-config. */
    tuning: SignalTuning;
    /** The resolved global date range (ISO) the gateway applies to every query — for loaders that fetch outside the gateway (e.g. cost records) so their window matches. */
    range: { start: string; end: string };
    /** The global range as a day count — for per-window math (weeks, denominators) and captions, matching what the gateway queries over. */
    windowDays: number;
}

/** Declarative definition of one landing-page signal tile. */
export interface SignalTileDef {
    id: string;
    title: string;
    description: string;
    icon: LucideIcon;
    accent: VizAccent;
    kind: SignalKind;
    /** Grid span; defaults to 1. A wide tile (2) suits heatmaps and dense alert lists. */
    span?: 1 | 2;
    /** Optional drill-in destination (e.g. an existing topic page). */
    to?: string;
    /** Deterministic illustrative data, seeded by the tile id — always rendered first. */
    sample: (rng: SignalRng) => SignalData;
    /** Optional live loader; its result replaces the sample. On throw, the sample is kept. */
    live?: (ctx: SignalContext) => Promise<SignalData>;
}
